import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import apiClient from "@/api/client";
import type { IngestAttachment, UploadUrlResponse } from "@/types";

interface IngestListResponse {
  attachments: IngestAttachment[];
}

const ingestKey = ["kb", "ingest"] as const;

/**
 * Lists the requesting user's recent ingest uploads.
 *
 * Auto-refreshes every 5s while any row is in pending/processing —
 * once everything is in a terminal state (done/failed), polling stops
 * to avoid hammering the API.
 */
export function useIngestAttachments() {
  return useQuery<IngestAttachment[]>({
    queryKey: ingestKey,
    queryFn: async () => {
      const res = await apiClient.get<IngestListResponse>("/kb/ingest");
      return res.data.attachments;
    },
    refetchInterval: (query) => {
      const data = query.state.data as IngestAttachment[] | undefined;
      if (!data) return false;
      const stillWorking = data.some(
        (a) => a.processing_status === "pending" || a.processing_status === "processing",
      );
      return stillWorking ? 5000 : false;
    },
  });
}

/**
 * Two-step upload: backend mints a signed URL + writes the pending row,
 * then the browser PUTs the file bytes directly to GCS. The mutation
 * returns the attachment_id; the list poll picks up its status.
 */
export function useIngestUpload() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (file: File) => {
      const startResponse = await apiClient.post<UploadUrlResponse>("/kb/ingest/upload-url", {
        file_name: file.name,
        mime_type: file.type || "application/octet-stream",
        file_size_bytes: file.size,
      });
      const { upload_url, attachment_id } = startResponse.data;

      // Direct PUT to GCS — bypass apiClient (different host, no auth header).
      const putResponse = await fetch(upload_url, {
        method: "PUT",
        headers: { "Content-Type": file.type || "application/octet-stream" },
        body: file,
      });
      if (!putResponse.ok) {
        throw new Error(`Upload to storage failed: HTTP ${putResponse.status}`);
      }
      return { attachment_id, file_name: file.name };
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ingestKey }),
  });
}
